import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";


// 📌 Create Enquiry (User)
export const createEnquiry = createAsyncThunk(
  "businessEnquiry/createEnquiry",
  async (enquiryData, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/create-enquiry`, enquiryData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Server error" });
    }
  }
);

// 📌 Get Business Enquiries (Business Owner)
export const getBusinessEnquiries = createAsyncThunk(
  "businessEnquiry/getBusinessEnquiries",
  async (businessId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/business-enquiries/${businessId}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Server error" });
    }
  }
);

// 📌 Get All Enquiries (Admin)
export const getAllEnquiries = createAsyncThunk(
  "businessEnquiry/getAllEnquiries",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/all-business-enquiries`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Server error" });
    }
  }
);

// 📌 Mark Enquiry Checked
export const markEnquiryChecked = createAsyncThunk(
  "businessEnquiry/markEnquiryChecked",
  async (enquiryId, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`/mark-check-enquiry/${enquiryId}`);
      return { ...data, enquiryId };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Server error" });
    }
  }
);

// 📌 Delete Enquiry
export const deleteEnquiry = createAsyncThunk(
  "businessEnquiry/deleteEnquiry",
  async (enquiryId, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(`/delete-enuiry/${enquiryId}`);
      return { ...data, enquiryId };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Server error" });
    }
  }
);

const businessEnquirySlice = createSlice({
  name: "businessEnquiry",
  initialState: {
    loading: false,
    error: null,
    successMessage: null,
    businessEnquiries: [], // For business owner
    allEnquiries: [], // For admin
  },
  reducers: {
    clearMessages(state) {
      state.error = null;
      state.successMessage = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Create Enquiry
      .addCase(createEnquiry.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createEnquiry.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.successMessage = action.payload.message;
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(createEnquiry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
      })

      // Get Business Enquiries
      .addCase(getBusinessEnquiries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBusinessEnquiries.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.businessEnquiries = action.payload.data;
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(getBusinessEnquiries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
      })

      // Get All Enquiries (Admin)
      .addCase(getAllEnquiries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllEnquiries.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.allEnquiries = action.payload.data;
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(getAllEnquiries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
      })

      // Mark Enquiry Checked
      .addCase(markEnquiryChecked.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.allEnquiries = state.allEnquiries.map((enq) =>
            enq.enquiryId === action.payload.enquiryId ? { ...enq, checked: true } : enq
          );
          state.successMessage = action.payload.message;
        } else {
          state.error = action.payload.message;
        }
      })

      // Delete Enquiry
      .addCase(deleteEnquiry.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.allEnquiries = state.allEnquiries.filter((enq) => enq.enquiryId !== action.payload.enquiryId);
          state.successMessage = action.payload.message;
        } else {
          state.error = action.payload.message;
        }
      });
  }
});

export const { clearMessages } = businessEnquirySlice.actions;
export default businessEnquirySlice.reducer;
